/**
 * AgentRunAwaitingPanel —— 问答应答区。
 *
 * status=awaiting_input 时呈现当前问题：question 正文 + questionType 徽标
 * （信息类 / 授权类）+ options 快捷选择（点击填入应答框）+ 应答输入框 + 提交
 * （POST /agent-runs/{id}/input）。提交成功后等待服务端续跑（事件流将 run 状态
 * 切回 running），期间面板呈现「已提交，等待继续执行」。
 */

import { useEffect, useState } from "react";
import { Button, Input, Tag, Typography, message } from "antd";
import { useSubmitAgentRunInput } from "../../hooks/useAgentRuns";
import { agentRunQuestionTypeLabel } from "../../types/agentRun";
import { mapApiError } from "../../utils/api-error";

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

export interface AgentRunAwaitingPanelProps {
  runId: string;
  question: string;
  questionType: string | null;
  options: string[] | null;
}

export default function AgentRunAwaitingPanel({
  runId,
  question,
  questionType,
  options,
}: AgentRunAwaitingPanelProps) {
  const [answer, setAnswer] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const submitMut = useSubmitAgentRunInput(runId);
  const [messageApi, contextHolder] = message.useMessage();

  // 新问题到达（或状态离开 awaiting）时重置本地草稿。
  useEffect(() => {
    setAnswer("");
    setSubmitted(false);
  }, [question]);

  const handleSubmit = async () => {
    const trimmed = answer.trim();
    if (!trimmed) {
      messageApi.warning("请输入应答内容");
      return;
    }
    try {
      // 后端契约：answer 为结构化对象（自由文本放 {"text"}），不是裸字符串。
      await submitMut.mutateAsync({ answer: { text: trimmed } });
      setSubmitted(true);
    } catch (err) {
      messageApi.error(mapApiError(err));
    }
  };

  return (
    <div
      data-testid="agent-run-awaiting-panel"
      style={{
        padding: "var(--jerry-sp-4)",
        border: "1px solid var(--jerry-primary-border)",
        borderRadius: 8,
        background: "var(--jerry-primary-subtle)",
      }}
    >
      {contextHolder}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--jerry-sp-2)", marginBottom: "var(--jerry-sp-2)" }}>
        <Tag color="purple" data-testid="agent-run-question-type">
          {agentRunQuestionTypeLabel(questionType)}
        </Tag>
        <Text strong style={{ color: "var(--jerry-text-primary)" }}>
          任务等待你的应答
        </Text>
      </div>
      <Paragraph style={{ marginBottom: "var(--jerry-sp-3)", color: "var(--jerry-text-primary)" }}>
        {question}
      </Paragraph>

      {submitted ? (
        <Text type="secondary" data-testid="agent-run-awaiting-submitted">
          已提交，等待任务继续执行…
        </Text>
      ) : (
        <>
          {options && options.length > 0 && (
            <div data-testid="agent-run-question-options" style={{ display: "flex", gap: "var(--jerry-sp-2)", flexWrap: "wrap", marginBottom: "var(--jerry-sp-3)" }}>
              {options.map((option) => (
                <Button
                  key={option}
                  size="small"
                  data-testid={`agent-run-question-option-${option}`}
                  onClick={() => setAnswer(option)}
                >
                  {option}
                </Button>
              ))}
            </div>
          )}
          <TextArea
            rows={3}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="输入应答内容，提交后任务将继续执行"
            maxLength={2000}
            data-testid="agent-run-answer-input"
          />
          <div style={{ marginTop: "var(--jerry-sp-3)", textAlign: "right" }}>
            <Button
              type="primary"
              loading={submitMut.isPending}
              onClick={handleSubmit}
              data-testid="agent-run-answer-submit"
            >
              提交应答
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
